<template>
    <div class="appointments-records">
        <STBaseButton text="Записаться в офис"
                      icon-after="IconRightFill"
                      icon-after-modify="icon-fill icon_color_white"
                      modify="button_full_width"
                      @click="createNewAppointment" />

        <div v-if="records?.length"
             class="appointments-records__annotation">
            У вас запланировано {{ records.length }} {{ _declineVisits(records.length) }} офиса
        </div>

        <div v-if="isPageLoaded"
             class="appointments-records__list">
            <AppointmentsRecord v-for="record in records"
                                :key="record.visitRecordId"
                                :visit-record-id="record.visitRecordId"
                                :project-data="getRecordDataForComplex(record.projectId)"
                                :visit-start="record.start"
                                :phone="record.clientPhone"
                                :client-name="record.clientName"
                                :client-surname="record.clientSurname"
                                :comment="record.comment"
                                @attemptToCancelRecord="attemptToCancelRecord"
                                @changeRecord="(id, title) => changeRecord(id, title, record.projectId, record.clientPhone, record.clientName, record.clientSurname, record.comment)" />
        </div>
        <template v-else>
            <div class="appointments-records__loading">
                <IconLoadGrey class="appointments-records__loader" />
                <div class="appointments-records__loading-text">
                    Получаем данные о комплексах...
                </div>
            </div>
        </template>

        <STPopup ref="cancelAppointmentsPopup"
                 :icon-close-big="false"
                 modify="popup_middle_size popup_center_position">
            <AppointmentsCancel :cancel-id="cancelId"
                                :is-deleting="isDeleting"
                                @submitRecordCancelling="submitRecordCancelling"
                                @close="closeAppointmentsCancelPopup" />
        </STPopup>
    </div>
</template>

<script>
import AppointmentsCancel from '@/blocks/AppointmentsBlock/AppointmentsCancel/AppointmentsCancel';
import AppointmentsRecord from '@/components/AppointmentsRecord/AppointmentsRecord';
import IconLoadGrey from '@/components/Icons/LoadGrey';

import {mapActions, mapGetters} from 'vuex';

export default {
    name: 'AppointmentsRecords',

    components: {
        AppointmentsCancel,
        AppointmentsRecord,
        IconLoadGrey
    },

    props: {
        /**
         * Массив объектов:
         * {
         *    "clientName": String,
         *    "clientSurname": String,
         *    "clientPhone": String
         *    "start": String
         *    "finish": String
         *    "projectId": String
         *    "visitRecordId": String
         * }[]
        */
        records: [Array]
    },

    data() {
        return {
            isComplexesLoaded: false,
            isProjectsLoaded : false,
            isDeleting       : false,
            cancelId         : null
        };
    },

    computed: {
        ...mapGetters({
            complexes: 'GET_DISTRICT_LIST',
            projects : 'GET_PROJECTS'
        }),

        isPageLoaded() {
            return this.isComplexesLoaded && this.isProjectsLoaded;
        }
    },

    async created() {
        if (!this.records?.length) {
            this.isComplexesLoaded = true;
            this.isProjectsLoaded = true;
            return;
        }

        if (!this.complexes?.length) {
            await this.getComplexData();
        } else {
            this.isComplexesLoaded = true;
        }

        if (!this.projects?.length) {
            await this.getProjectsInfo();
        }

        this.isProjectsLoaded = true;
    },

    methods: {
        ...mapActions(['getComplexes', 'getProjectsInfo', 'deleteAppointment']),

        async getComplexData() {
            try {
                await this.getComplexes();

                this.isComplexesLoaded = true;

                if (!this.complexes?.length) {
                    console.error('Комплексы отсутствуют');
                }
            } catch (error) {
                this.isComplexesLoaded = true;
                console.error('Произошла ошибка при запросе комплексов', JSON.stringify(error));
            }
        },

        getRecordDataForComplex(projectId) {
            const complexData = {
                name   : '',
                address: '',
                link   : ''
            };

            if (!this.complexes?.length || !projectId) {
                return complexData;
            }

            const complex = this.complexes.find((complex) => complex.id?.toLowerCase() === projectId.toLowerCase());

            if (!complex) {
                return complexData;
            }

            complexData.name = complex.name || '';
            complexData.address = complex.broker_address || '';

            if (!this.projects?.length) {
                return complexData;
            }

            const project = this.projects.find((project) => project.id?.toLowerCase() === projectId.toLowerCase());

            if (!project) {
                return complexData;
            }

            complexData.link = project?.link || '';

            return complexData;
        },

        createNewAppointment() {
            this.$emit('create');
        },

        changeRecord(id = null, title = null, projectId = null, clientPhone = null, clientName = null, clientSurname = null, comment = null) {
            if (!id) {
                console.error('Для изменения записи не хватает ID записи');
                return;
            }

            if (!title) {
                console.error('Для изменения записи не хватает даты и времени записи');
                return;
            }

            if (!projectId) {
                console.error('Для изменения записи не хватает ID проекта');
                return;
            }

            if (!clientPhone) {
                console.error('Для изменения записи не хватает номера телефона клиента');
                return;
            }

            this.$emit('change', id, title, projectId, clientPhone, clientName, clientSurname, comment);
        },

        attemptToCancelRecord(id = null) {
            if (!id) {
                console.error('Для отмены записи не хватает ID записи');
                return;
            }

            this.cancelId = id;
            this.$refs.cancelAppointmentsPopup.open();
        },

        async submitRecordCancelling() {
            if (this.cancelId) {
                this.isDeleting = true;

                try {
                    await this.deleteAppointment({
                        visitRecordId: this.cancelId
                    });

                    this.isDeleting = false;
                } catch (error) {
                    this.isDeleting = false;
                    console.error(`Произошла ошибка при удалении записи: ${JSON.stringify(error)}`);
                }
            }

            this.cancelId = null;
            this.$emit('update');
            this.closeAppointmentsCancelPopup();
        },

        closeAppointmentsCancelPopup() {
            this.cancelId = null;
            this.$refs.cancelAppointmentsPopup.close();
        },

        _declineVisits(count) {
            const cases = ['посещение', 'посещения', 'посещений'];
            const mod = count % 10;
            const mod100 = count % 100;

            if (mod === 1 && mod100 !== 11) {
                return cases[0]; // 1 посещение
            } else if (mod >= 2 && mod <= 4 && (mod100 < 12 || mod100 > 14)) {
                return cases[1]; // 2-4 посещения
            } else {
                return cases[2]; // остальные случаи
            }
        }
    }
};
</script>

<style lang="scss">
@import "AppointmentsRecords";
</style>
